import { matchPath } from 'react-router-dom';

/**
 * A detail screen whose own record the API refuses goes to the 404 page.
 *
 * The backend answers 404 (or 403) for a record outside the caller's reach —
 * another department's employee, another company's invoice — the same way it
 * answers a missing id. Each detail screen used to carry on regardless and
 * render its empty frame (blank header, today's date, empty tabs), some with a
 * toast and some without. This decides it in one place, from the axios
 * interceptor, so every detail screen behaves alike.
 *
 * Only the GET that loads the screen's own record counts: a 404 from a tab or
 * a side panel of a record the caller can see must not take them off it.
 */

export const NOT_FOUND_PATH = '/error-pages/error-404';

/** Each detail route and the GET that loads its record, in route params. */
const DETAIL_RECORDS: ReadonlyArray<{ route: string; api: string }> = [
  {
    route: '/employees-subcontractors/:employeeId',
    api: 'subcontractor/:employeeId',
  },
  { route: '/companies/:companyId', api: 'company/:companyId' },
  { route: '/agency/:agencyId', api: 'agency/:agencyId' },
  { route: '/assignments/:assignmentId', api: 'assignment/:assignmentId' },
  { route: '/invoices/invoice/:id', api: 'invoice/:id' },
  { route: '/invoices/credit-note/:id', api: 'invoice/:id' },
  { route: '/payroll/:companyId', api: 'payroll-overview/:companyId' },
  {
    route: '/payroll/:companyId/employee/:draftPayrollId',
    api: 'draft-payroll/:draftPayrollId',
  },
  { route: '/reports/payroll/:payrollId', api: 'payroll/:payrollId' },
  { route: '/payslips/:id', api: 'subcontractor/self-bill/:id/detail' },
  { route: '/rti-filings/fps/:id', api: 'rti/fps/:id' },
  { route: '/rti-filings/eps/:id', api: 'rti/eps/:id' },
  { route: '/rti-filings/cis-300/:id', api: 'rti/cis-300/:id' },
  {
    route: '/revenue-filings/psr-submissions/:id',
    api: 'payroll/psr-submission/batch/:id',
  },
  { route: '/statutory-leave/:type/:id', api: 'statutory/:type/:id' },
  { route: '/statutory-payment/:type/:id', api: 'statutory/:type/:id' },
  { route: '/timesheets/:id', api: 'time-sheet/:id' },
  { route: '/timesheets-agency/:id', api: 'time-sheet/:id' },
  { route: '/timesheets-employee/:id', api: 'time-sheet/:id' },
  { route: '/bulk-update/:id', api: 'bulk-update/import/subcontractors/:id' },
];

/** The request path without origin, query or outer slashes, decoded. */
const requestPathOf = (url?: string): string => {
  if (!url) return '';
  const path = url
    .replace(/^[a-z][a-z0-9+.-]*:\/\/[^/]+/i, '')
    .split(/[?#]/)[0]
    .replace(/^\/+|\/+$/g, '');
  try {
    return decodeURIComponent(path);
  } catch {
    return path;
  }
};

interface FailedRequest {
  response?: { status?: number };
  config?: { method?: string; url?: string };
}

/**
 * True when the failed request is the GET of the record the screen at
 * `pathname` shows, and the API refused it as missing or out of reach.
 */
export function isDetailRecordRefused(
  error: FailedRequest | undefined,
  pathname: string,
): boolean {
  const status = error?.response?.status;
  if (status !== 404 && status !== 403) return false;
  if ((error?.config?.method ?? 'get').toLowerCase() !== 'get') return false;
  const requested = requestPathOf(error?.config?.url);
  if (!requested) return false;
  return DETAIL_RECORDS.some(({ route, api }) => {
    const match = matchPath({ path: route, end: true }, pathname);
    if (!match) return false;
    const expected = api.replace(
      /:([A-Za-z]+)/g,
      (_, name: string) => match.params[name] ?? '',
    );
    return requested === expected || requested.endsWith(`/${expected}`);
  });
}

/**
 * Bridge to the router, as for the session expiry: the interceptor is a plain
 * module with no router, so the app registers how to navigate on mount.
 */
type NotFoundHandler = (path: string) => void;

let handler: NotFoundHandler | null = null;

export function registerRecordNotFoundHandler(fn: NotFoundHandler): () => void {
  handler = fn;
  return () => {
    if (handler === fn) handler = null;
  };
}

/** Leaves the detail screen for the 404 page, replacing it in the history. */
export function showRecordNotFound(): void {
  if (handler) handler(NOT_FOUND_PATH);
  else window.location.replace(NOT_FOUND_PATH);
}
